// src/components/Partners.jsx

import React, { useState, useEffect } from 'react'
import { db } from '../firebase'
import { collection, getDocs } from 'firebase/firestore'
import styles from './partners.module.css'
import { initHfClient } from './partners-hf'
import {
  FaSearch,
  FaUser,
  FaRobot,
  FaBuilding,
  FaStar,
} from 'react-icons/fa'
import PartnersListMe from './Partners-listme'

// 1. EXISTING STATIC CARD DATA
const CARD_DATA = [
  {
    title: 'YouTube Influencer',
    icon: <FaUser />,
    tags: ['#creator', 'video'],
    rating: 85,
    description:
      'Growth-focused creators who can showcase your product to their engaged audiences through authentic reviews and tutorials.',
    primaryColor: '#1e40af',
  },
  {
    title: 'SEO AI Agent',
    icon: <FaRobot />,
    tags: ['#search', 'seo', 'optimization'],
    rating: 92,
    description:
      'Automates keyword research, content optimization, and backlink strategies to improve your organic search rankings and traffic.',
    primaryColor: '#166534',
  },
  {
    title: 'Tech Startups',
    icon: <FaBuilding />,
    tags: ['innovation', 'launch support'],
    rating: 88,
    description:
      'Emerging companies with complementary products that might be interested in partnerships, integrations, or co-marketing.',
    primaryColor: '#6d28d9',
  },
]

// 2. Turn existing static data into "profiles"
const staticProfiles = CARD_DATA.map((c, i) => ({
  id: `static-${i}`,
  entityName: c.title,
  email: '',
  entity: 'Individual',
  skill: c.description,
  goal: '',
  resources: '',
  tags: c.tags,
  rating: c.rating,
  icon: c.icon,
  primaryColor: c.primaryColor,
}))

// 3. Map each entity type to icon & color
const ENTITY_CONFIG = {
  Individual: { icon: <FaUser />, color: '#1e40af' },
  Company:    { icon: <FaBuilding />, color: '#6d28d9' },
  'AI Agent': { icon: <FaRobot />, color: '#166534' },
}

export default function Partners() {
  // initialize HF client + load profiles
  const [profiles, setProfiles] = useState([])
  useEffect(() => {
    initHfClient(process.env.REACT_APP_HF_TOKEN)
    loadProfiles()
  }, [])

 const loadProfiles = async () => {
  const snap = await getDocs(collection(db, 'profiles'))
  const mapped = snap.docs.map(d => ({ id: d.id, ...d.data() }))
  console.log('Loaded profiles:', mapped)
  setProfiles(mapped)
}


  // search & goals
  const [goals, setGoals] = useState([])
  const [inputValue, setInputValue] = useState('')
  const handleKeyDown = e => {
    if (e.key === 'Enter' && inputValue.trim()) {
      const trimmed = inputValue.trim()
      if (!goals.includes(trimmed)) setGoals([...goals, trimmed])
      setInputValue('')
    }
  }
  const removeGoal = g => setGoals(goals.filter(x => x !== g))

  // modal open state
  const [isModalOpen, setModalOpen] = useState(false)

  // combine dynamic + static
  const allProfiles = [...profiles, ...staticProfiles]
  const matchCount = CARD_DATA.length + profiles.length

  // dropdown
  const [openResourcesId, setOpenResourcesId] = useState(null)

  return (
    <div className={styles.container}>
      {/* Banner */}
      <div className={styles.bannerContainer}>
        <img
          src="/images/partnerbanner.png"
          alt="Banner"
          className={styles.banner}
        />
      </div>

      {/* Search Bar */}
      <header className={styles.header}>
        <div className={styles.logo}>Lex</div>
        <div className={styles.search}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Type a goal and hit Enter"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </header>

      {/* Resources + Actions */}
      <div className={styles.resourcesMatchedRow}>
        <div className={styles.resourcesMatched}>
          <FaUser />
          <span>Resources matched</span>
          <span className={styles.matchCount}>{matchCount}</span>
        </div>
        <div className={styles.actionButtons}>
          <button className={styles.textButton}>⟳ Shuffle Deck</button>
          <button className={styles.textButton}>📄 Draw 3</button>
          <button
            className={styles.primaryButton}
            onClick={() => setModalOpen(true)}
          >
            List me here
          </button>
        </div>
      </div>

      {/* Goals tags */}
      <div className={styles.goalsContainer}>
        {goals.map(g => (
          <div key={g} className={styles.goalChip}>
            <span>{g}</span>
            <button
              className={styles.goalRemove}
              onClick={() => removeGoal(g)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Partner Cards */}
      <div className={styles.cards}>
        {allProfiles.map(p => {
          const cfg = ENTITY_CONFIG[p.entity] || {}
          const icon = p.icon || cfg.icon
          const color = p.primaryColor || cfg.color

          return (
            <div
              key={p.id}
              className={styles.card}
              style={{
                '--highlight-color': color,
                '--highlight-color-opaque': `${color}25`,
              }}
            >
              {/* Card Header */}
              <div className={styles.cardHeader}>
               <div className={styles.cardIcon}>{icon}</div>
               <h3 className={styles.cardTitle}>
               {p.entityName || p.name || 'Unnamed'}
              </h3>
              </div>

              {/* Rating */}
              {p.rating != null && (
                <div className={styles.rating}>
                  <FaStar />
                  <span>{p.rating}</span>
                </div>
              )}

              {/* Tags */}
              {p.tags && p.tags.length > 0 && (
                <div className={styles.tags}>
                  {p.tags.map(t => (
                    <span key={t} className={styles.tag}>{t}</span>
                  ))}
                </div>
              )}

              {/* Description */}
              <p className={styles.cardDescription}>{p.skill}</p>

              {/* Goal */}
              <p className={styles.cardGoal}>
                <strong>My Goal:</strong> {p.goal}
              </p>

              {/* Actions */}
              <div className={styles.cardActions}>
                <button
                  className={styles.outlineButton}
                  style={{ borderColor: color, color }}
                  onClick={() =>
                    setOpenResourcesId(openResourcesId === p.id ? null : p.id)
                  }
                >
                  Claim free resources
                </button>
                <button
                  className={styles.filledButton}
                  style={{ backgroundColor: color }}
                >
                  Co-pilot
                </button>
              </div>

              {/* Resource Dropdown */}
              {openResourcesId === p.id && (
                <div className={styles.resourceDropdown}>
                       {Array.isArray(p.resources)
       ? (p.resources.length
           ? p.resources.map((r, i) => (
               <div key={i}>
                 {r.text || 'No free resources listed'}
                 {r.qty && ` (${r.qty})`}
               </div>
             ))
           : 'No resources listed.')
       : (p.resources || 'No resources listed.')}


                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* New List-me Modal component */}
      <PartnersListMe
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        onProfileAdded={loadProfiles}
      />
    </div>
  )
}
